/* Shared helpers for the Wix tools (page checker, redirect builder, SEO pattern
   preview, indexing decoder) and the guides that go with them. Pure functions:
   no DOM, no network, no storage, so the page renderers (*.cjs), the browser
   scripts and the tests all run the same code. The browser loads this file
   first (site.cjs: microTool.shared) and reads it from window.WixShared. */
(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.WixShared = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const esc = value =>
    String(value).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  // ---- Text -----------------------------------------------------------------

  // Words people type often pick up non-breaking spaces and zero-width marks
  // from spreadsheets, Word and web pages. Treat them as the plain things they stand for.
  function tidyText(value) {
    return String(value == null ? '' : value)
      .replace(/[   ]/g, ' ')
      .replace(/[​‌‍⁠﻿]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  const graphemes = text => {
    const value = String(text);
    if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
      return Array.from(new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(value), part => part.segment);
    }
    return Array.from(value);
  };

  // ---- Measuring text the way a search result draws it -----------------------

  // Arial advance widths (1/1000 em) for printable ASCII. Google draws result
  // titles and descriptions in a sans-serif close to Arial, and cuts them by
  // width, not by characters. Used when no canvas is available (server render,
  // tests) and as the first paint before the browser measures.
  const ASCII = [278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278, 584, 584, 584, 556, 1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833, 722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556, 333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556, 556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334, 260, 334, 584];
  const TYPOGRAPHIC = { '‘': 222, '’': 222, '“': 333, '”': 333, '–': 556, '—': 1000, '…': 1000, '•': 350, '€': 556, '£': 556, '©': 737, '®': 737, '™': 1000, ' ': 278 };
  const EMOJI = /\p{Extended_Pictographic}/u;

  function glyphWidth(part) {
    if (EMOJI.test(part)) return 1230;
    if (TYPOGRAPHIC[part] !== undefined) return TYPOGRAPHIC[part];
    const code = part.codePointAt(0);
    if (code >= 32 && code <= 126 && part.length === 1) return ASCII[code - 32];
    const base = part.normalize('NFD').charAt(0);
    const baseCode = base.charCodeAt(0);
    if (baseCode >= 32 && baseCode <= 126) return ASCII[baseCode - 32];
    if (code >= 0x2e80) return 1000;
    return 556;
  }

  // Width in CSS pixels of `text` in an Arial-like face. spec: { size, weight }.
  function approximateWidth(text, spec = {}) {
    const size = spec.size || 20;
    let units = 0;
    graphemes(text).forEach(part => { units += glyphWidth(part); });
    const weight = (spec.weight || 400) >= 700 ? 1.07 : 1;
    return units / 1000 * size * weight;
  }

  // A measuring function: the browser's canvas when there is one (the visitor's
  // own fonts), otherwise the Arial table above. Both take (text, spec).
  function makeMeasure(doc) {
    try {
      const canvas = doc && doc.createElement && doc.createElement('canvas');
      const context = canvas && canvas.getContext && canvas.getContext('2d');
      if (context && typeof context.measureText === 'function') {
        const cache = new Map();
        let current = '';
        return (text, spec = {}) => {
          const css = `${spec.weight || 400} ${spec.size || 20}px ${spec.family || 'Arial, Helvetica, sans-serif'}`;
          const key = css + '\u0000' + text;
          const known = cache.get(key);
          if (known !== undefined) return known;
          if (css !== current) { context.font = css; current = css; }
          const width = context.measureText(text).width;
          if (cache.size > 4000) cache.clear();
          cache.set(key, width);
          return width;
        };
      }
    } catch (_) { /* fall through to the table */ }
    return approximateWidth;
  }

  // ---- CSV -------------------------------------------------------------------

  // Picks the delimiter a pasted table most likely uses. Spreadsheets paste as
  // tabs; many countries' Excel saves semicolons; everything else is commas.
  function detectDelimiter(text) {
    const sample = String(text).replace(/^﻿/, '').split(/\r\n|\n|\r/).filter(line => line.trim()).slice(0, 5);
    const counts = { '\t': 0, ',': 0, ';': 0 };
    for (const line of sample) {
      let quoted = false;
      for (const char of line) {
        if (char === '"') quoted = !quoted;
        else if (!quoted && char in counts) counts[char]++;
      }
    }
    const best = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0];
    return counts[best] ? best : ',';
  }

  // RFC 4180-style parser: quotes, doubled quotes, newlines inside quotes, CRLF.
  function parseDelimited(text, delimiter) {
    const input = String(text).replace(/^﻿/, '');
    const sep = delimiter || detectDelimiter(input);
    const rows = [];
    let row = [];
    let field = '';
    let quoted = false;
    let touched = false;
    for (let i = 0; i < input.length; i++) {
      const char = input[i];
      if (quoted) {
        if (char === '"') {
          if (input[i + 1] === '"') { field += '"'; i++; } else quoted = false;
        } else field += char;
      } else if (char === '"' && field === '') {
        quoted = true;
        touched = true;
      } else if (char === sep) {
        row.push(field); field = ''; touched = true;
      } else if (char === '\n' || char === '\r') {
        if (char === '\r' && input[i + 1] === '\n') i++;
        if (touched || field !== '' || row.length) { row.push(field); rows.push(row); }
        row = []; field = ''; touched = false;
      } else {
        field += char;
        touched = true;
      }
    }
    if (touched || field !== '' || row.length) { row.push(field); rows.push(row); }
    return rows;
  }

  function csvField(value, delimiter = ',') {
    const text = value == null ? '' : String(value);
    return text === '' || !(text.includes(delimiter) || /["\r\n]/.test(text) || /^\s|\s$/.test(text))
      ? text
      : '"' + text.replace(/"/g, '""') + '"';
  }

  function toCsv(rows, { eol = '\r\n', delimiter = ',', bom = false } = {}) {
    return (bom ? '﻿' : '') + rows.map(row => row.map(value => csvField(value, delimiter)).join(delimiter)).join(eol) + eol;
  }

  // Outline icons (24px grid, 1.6 stroke) for the four Wix tools, shared by the
  // /tools directory and the Wix hub so they cannot drift.
  const ICON_PATHS = Object.freeze({
    page: '<path d="M7 3h7l4 4v14H7z"/><path d="M14 3v4h4"/><path d="m9.5 14 2 2 3.5-4"/>',
    redirects: '<path d="M5 8h9a4 4 0 0 1 4 4v4"/><path d="m15 13 3 3 3-3"/><circle cx="5" cy="8" r="1.1"/>',
    patterns: '<path d="M9 4C7 4 6 5 6 7v2c0 1.5-1 2.5-2 3 1 .5 2 1.5 2 3v2c0 2 1 3 3 3"/><path d="M15 4c2 0 3 1 3 3v2c0 1.5 1 2.5 2 3-1 .5-2 1.5-2 3v2c0 2-1 3-3 3"/>',
    indexing: '<path d="M4 5h16l-6 7v6l-4 2v-8z"/>',
    policy: '<path d="M12 3 5 6v5.5c0 4.4 3 7.7 7 9.5 4-1.8 7-5.1 7-9.5V6z"/><path d="m9 12 2.2 2.2L15 10.4"/>'
  });

  return Object.freeze({
    ICON_PATHS,
    esc,
    tidyText,
    graphemes,
    approximateWidth,
    makeMeasure,
    detectDelimiter,
    parseDelimited,
    toCsv
  });
});
